/**
 * Wire contracts. Both the API and the web client validate against these, so a change
 * to a payload shape is a compile error on both sides rather than a runtime surprise.
 */
import { z } from 'zod';
import { MILEAGE_BANDS, PACKAGE_CATEGORIES, PRICING_MODES, PRICING_ZONES, SERVICE_LEVELS, VEHICLE_CLASSES, resolvePricingSegment, } from './pricing.js';
import { DELIVERY_STATUSES } from './delivery-state.js';
import { ROLES } from './roles.js';
export const serviceLevelSchema = z.enum(SERVICE_LEVELS);
export const vehicleClassSchema = z.enum(VEHICLE_CLASSES);
export const pricingModeSchema = z.enum(PRICING_MODES);
export const deliveryStatusSchema = z.enum(DELIVERY_STATUSES);
export const roleSchema = z.enum(ROLES);
export const pricingZoneSchema = z.enum(PRICING_ZONES);
export const mileageBandSchema = z.enum(MILEAGE_BANDS);
/**
 * Admin-editable pricing overrides, written to `system_settings` and deep-merged over
 * IECN_RATE_CARD by `pricingRules()` in the API. Every field is optional — a partial
 * override — but whatever IS present is shape- and range-checked here. A malformed value
 * reaching the database would otherwise silently break every quote once the 30s settings
 * cache expires, with no error anywhere.
 */
const centsSchema = z.number().int().min(0).max(1_000_000);
const percentSchema = z.number().min(0).max(100);
const centsByServiceLevelSchema = z.record(serviceLevelSchema, centsSchema);
const centsByVehicleClassSchema = z.record(vehicleClassSchema, centsSchema);
const mileageBandSpecSchema = z
    .object({
    key: mileageBandSchema,
    label: z.string().min(1),
    fromMiles: z.number().min(0),
    toMiles: z.number().min(0).nullable(),
    bandLabel: z.string().min(1),
    timeframe: z.record(serviceLevelSchema, z.string()),
    ratePerMileCents: centsByServiceLevelSchema,
})
    .partial({ label: true, bandLabel: true, timeframe: true });
const volumeTierSchema = z.object({
    minStops: z.number().int().min(1),
    maxStops: z.number().int().min(1).nullable(),
    flatCents: centsSchema,
    perAdditionalStopCents: centsSchema,
    label: z.string().min(1).optional(),
});
export const pricingRuleSetUpdateSchema = z
    .object({
    version: z.string().min(1),
    effectiveFrom: z.string().min(1),
    currency: z.string().length(3),
    source: z.string().min(1),
    matrixCents: z.record(pricingZoneSchema, centsByServiceLevelSchema),
    overagePerMileCents: centsByServiceLevelSchema,
    overageAfterMiles: z.number().min(0),
    surcharges: z
        .object({
        afterHoursCents: centsSchema,
        weekendHolidayMultiplier: z.number().min(1).max(5),
        temperatureControlledCents: centsSchema,
        fuelSurchargePercent: percentSchema,
        additionalStopCents: centsSchema,
        vehicleSurchargeCents: centsByVehicleClassSchema,
    })
        .partial(),
    waitTime: z
        .object({
        freeMinutes: z.number().int().min(0),
        perMinuteCents: centsSchema,
    })
        .partial(),
    businessHours: z
        .object({
        timezone: z.string().min(1),
        weekdays: z.array(z.number().int().min(0).max(6)),
        startMinuteOfDay: z.number().int().min(0).max(1439),
        endMinuteOfDay: z.number().int().min(0).max(1439),
    })
        .partial(),
    minimumChargeCents: z.record(pricingZoneSchema, centsSchema),
    driverPay: z
        .object({
        baseCents: centsSchema,
        lineHaulSharePercent: percentSchema,
        waitTimeSharePercent: percentSchema,
        perStopCents: centsSchema,
        minimumPerDeliveryCents: centsSchema,
        confirmedByOperator: z.boolean(),
    })
        .partial(),
    processorFeePercent: percentSchema,
    processorFeeFixedCents: centsSchema,
    minimumMarginPercent: percentSchema,
    quoteValidMinutes: z.number().min(1),
    bundlePricing: z
        .object({
        enabled: z.boolean(),
        tiers: z.array(volumeTierSchema).min(1),
        includedMilesPerStop: z.number().min(0),
        perMileOverCents: centsSchema,
        allowedServiceLevels: z.array(serviceLevelSchema),
        /** Discounted STANDARD-segment ladder. Null clears the override back to `tiers`. */
        standardTiers: z.array(volumeTierSchema).min(1).nullable(),
    })
        .partial(),
    perMile: z
        .object({
        enabled: z.boolean(),
        bands: z.array(mileageBandSpecSchema).min(1),
        extendedDurationMinutes: z.number().min(0),
        extendedSurchargePerMileCents: centsSchema,
        overnightPerMileCents: centsSchema,
        minimumChargeCents: centsByServiceLevelSchema,
        /** Discounted STANDARD-segment LOCAL-band rate. Null clears the override. */
        standardLocalRatePerMileCents: centsByServiceLevelSchema.nullable(),
    })
        .partial(),
})
    .partial()
    .strict();
export const latLngSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
/**
 * Phone numbers arrive in every shape a human can type — (470) 555-0111, 470.555.0111,
 * +1 470 555 0111. Normalise to E.164 rather than rejecting a valid number for its
 * punctuation; a courier booking a STAT run should not be fighting a form.
 */
export function normalizePhone(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    const hadPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15)
        return null;
    if (hadPlus)
        return `+${digits}`;
    // A bare 10-digit number is North American; 11 digits starting with 1 already carries it.
    if (digits.length === 10)
        return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1'))
        return `+${digits}`;
    return `+${digits}`;
}
export const phoneSchema = z
    .string()
    .trim()
    .transform((value, ctx) => {
    const normalized = normalizePhone(value);
    if (!normalized) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Enter a valid phone number, for example (470) 555-0111',
        });
        return z.NEVER;
    }
    return normalized;
});
export const emailSchema = z.string().trim().toLowerCase().email('Enter a valid email address');
export const addressInputSchema = z
    .object({
    /** Single-line address as typed or selected from autocomplete. */
    line1: z.string().trim().min(3, 'Address is required').max(200),
    line2: z.string().trim().max(120).optional().or(z.literal('')),
    city: z.string().trim().max(120).optional().or(z.literal('')),
    state: z.string().trim().max(40).optional().or(z.literal('')),
    postalCode: z.string().trim().max(20).optional().or(z.literal('')),
    country: z.string().trim().length(2).default('US'),
    /** Populated by geocoding or by the customer dropping a pin. */
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    /** Access detail that keeps a courier from being stuck at a loading dock. */
    unit: z.string().trim().max(60).optional().or(z.literal('')),
    accessNotes: z.string().trim().max(500).optional().or(z.literal('')),
})
    // A street line with no city is exactly the input shape that made a real quote geocode
    // to the wrong place (two different addresses collapsing onto the same fallback point).
    // A dropped pin already carries real coordinates, so it is exempt.
    .refine((value) => Boolean(value.city) || (value.lat !== undefined && value.lng !== undefined), {
    message: 'City is required so the route prices accurately',
    path: ['city'],
});
export const contactInputSchema = z.object({
    name: z.string().trim().min(2, 'Name is required').max(120),
    phone: phoneSchema,
    email: emailSchema.optional().or(z.literal('')),
    company: z.string().trim().max(160).optional().or(z.literal('')),
});
export const packageCategorySchema = z.enum(PACKAGE_CATEGORIES);
export const deliveryItemSchema = z.object({
    category: packageCategorySchema,
    description: z.string().trim().min(2, 'Describe what we are collecting').max(300),
    quantity: z.number().int().min(1).max(999).default(1),
    weightLbs: z.number().min(0).max(20000).optional(),
    lengthInches: z.number().min(0).max(600).optional(),
    widthInches: z.number().min(0).max(600).optional(),
    heightInches: z.number().min(0).max(600).optional(),
    declaredValueCents: z.number().int().min(0).max(100_000_00).optional(),
    fragile: z.boolean().default(false),
    temperatureSensitive: z.boolean().default(false),
    handlingNotes: z.string().trim().max(500).optional().or(z.literal('')),
});
/** POD requirements a customer or contract can demand. */
export const POD_REQUIREMENTS = [
    'PHOTO',
    'SIGNATURE',
    'PRINTED_NAME',
    'PIN',
    'BARCODE',
    'ID_CHECK',
];
export const podRequirementSchema = z.enum(POD_REQUIREMENTS);
/**
 * Sensible POD default given what's being carried — a courier collecting a specimen
 * needs stronger proof than one dropping off a document. Medical/pharmacy items add a
 * photo requirement on top of the standard signature; nothing else changes the default.
 */
export function defaultPodRequirements(items) {
    const base = ['SIGNATURE', 'PRINTED_NAME'];
    if (resolvePricingSegment(items) === 'MEDICAL')
        return [...base, 'PHOTO'];
    return base;
}
export const quoteRequestSchema = z.object({
    pickup: addressInputSchema,
    dropoff: addressInputSchema,
    serviceLevel: serviceLevelSchema,
    /**
     * Which published schedule to price against. Omitted on the initial /quotes call that
     * feeds the comparison screen — the customer has not chosen yet. Required when
     * persisting the final quote the booking is created from.
     */
    pricingMode: pricingModeSchema.optional(),
    vehicleClass: vehicleClassSchema.optional(),
    items: z.array(deliveryItemSchema).min(1).max(50).optional(),
    temperatureControlled: z.boolean().default(false),
    /** ISO 8601. Omit for ASAP. */
    scheduledFor: z.string().datetime().optional(),
    additionalStops: z.number().int().min(0).max(20).default(0),
    promoCode: z.string().trim().max(40).optional().or(z.literal('')),
});
export const priceLineSchema = z.object({
    code: z.string(),
    label: z.string(),
    amountCents: z.number().int(),
});
export const quoteResponseSchema = z.object({
    id: z.string(),
    reference: z.string(),
    expiresAt: z.string(),
    currency: z.string(),
    totalCents: z.number().int(),
    subtotalCents: z.number().int(),
    discountCents: z.number().int(),
    lines: z.array(priceLineSchema),
    miles: z.number(),
    zoneLabel: z.string(),
    serviceLevel: serviceLevelSchema,
    pricingMode: pricingModeSchema,
    distanceSource: z.enum(['routed', 'estimated']),
    recommendedVehicle: vehicleClassSchema,
    recommendedVehicleReason: z.string(),
    committedPickupBy: z.string(),
    committedDeliverBy: z.string(),
    serviceable: z.boolean(),
    serviceableMessage: z.string().optional(),
});
export const bookingRequestSchema = z.object({
    quoteId: z.string().min(1),
    pickupContact: contactInputSchema,
    dropoffContact: contactInputSchema,
    items: z.array(deliveryItemSchema).min(1).max(50),
    /** Shown to the courier. */
    pickupInstructions: z.string().trim().max(1000).optional().or(z.literal('')),
    dropoffInstructions: z.string().trim().max(1000).optional().or(z.literal('')),
    /** Shown to the customer on tracking. Never mixed with dispatch-internal notes. */
    customerReference: z.string().trim().max(120).optional().or(z.literal('')),
    purchaseOrder: z.string().trim().max(120).optional().or(z.literal('')),
    podRequirements: z.array(podRequirementSchema).default(['SIGNATURE', 'PRINTED_NAME']),
    /** Guest booking — who to send the receipt and tracking link to. */
    bookedByName: z.string().trim().min(2).max(120),
    bookedByEmail: emailSchema,
    bookedByPhone: phoneSchema,
    acceptedTermsVersion: z.string().min(1),
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(8, 'Password must be at least 8 characters').max(200),
});
export const registerSchema = loginSchema.extend({
    name: z.string().trim().min(2).max(120),
    phone: phoneSchema.optional().or(z.literal('')),
});
export const newPasswordSchema = z.string().min(8, 'Password must be at least 8 characters').max(200);
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: newPasswordSchema,
});
export const forgotPasswordSchema = z.object({
    email: emailSchema,
});
export const resetPasswordSchema = z.object({
    token: z.string().min(1),
    newPassword: newPasswordSchema,
});
export const driverLocationSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    accuracyMeters: z.number().min(0).max(10000).optional(),
    headingDegrees: z.number().min(0).max(360).optional(),
    speedMph: z.number().min(0).max(200).optional(),
    batteryPercent: z.number().min(0).max(100).optional(),
    /** Client timestamp — the server records its own receipt time separately. */
    recordedAt: z.string().datetime(),
    deliveryId: z.string().optional(),
});
export const podCaptureSchema = z.object({
    /** Base64 data URL of the signature (PNG) — optional when signature is not required. */
    signatureDataUrl: z.string().max(2_000_000).optional(),
    recipientName: z.string().trim().max(120).optional().or(z.literal('')),
    pin: z.string().trim().max(12).optional().or(z.literal('')),
    barcodeValue: z.string().trim().max(200).optional().or(z.literal('')),
    notes: z.string().trim().max(1000).optional().or(z.literal('')),
    photoIds: z.array(z.string()).max(10).default([]),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    /** Client-generated so an offline retry cannot create a duplicate POD. */
    clientEventId: z.string().min(8).max(80),
    capturedAt: z.string().datetime(),
});
export const FAILURE_REASONS = [
    'NO_ANSWER',
    'BUSINESS_CLOSED',
    'BAD_ADDRESS',
    'REFUSED',
    'NO_ACCESS',
    'UNSAFE_LOCATION',
    'RECIPIENT_NOT_AVAILABLE',
    'INCORRECT_RECIPIENT',
    'PACKAGE_ISSUE',
    'OTHER',
];
export const failureReasonSchema = z.enum(FAILURE_REASONS);
export const FAILURE_REASON_LABELS = {
    NO_ANSWER: 'No answer at the door',
    BUSINESS_CLOSED: 'Business closed',
    BAD_ADDRESS: 'Address is wrong or does not exist',
    REFUSED: 'Recipient refused the delivery',
    NO_ACCESS: 'Could not access the property',
    UNSAFE_LOCATION: 'Location is unsafe',
    RECIPIENT_NOT_AVAILABLE: 'Recipient not available',
    INCORRECT_RECIPIENT: 'Wrong recipient',
    PACKAGE_ISSUE: 'Problem with the package',
    OTHER: 'Something else',
};
export const failedAttemptSchema = z.object({
    reason: failureReasonSchema,
    notes: z.string().trim().max(1000).optional().or(z.literal('')),
    photoIds: z.array(z.string()).max(10).default([]),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    clientEventId: z.string().min(8).max(80),
});
export const assignDriverSchema = z.object({
    driverId: z.string().min(1),
    /** Version the dispatcher was looking at — rejects a stale reassignment. */
    expectedVersion: z.number().int().min(0),
    note: z.string().trim().max(500).optional().or(z.literal('')),
});
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
        requestId: z.string().optional(),
    }),
});
//# sourceMappingURL=schemas.js.map